import global from "../../../common/utility/global";

export function formatCurrency(value, moneda = "PEN") {
    const numero = Number(value) || 0;
    const simbolo = moneda === "USD" ? "US$" : "S/";
    return `${simbolo} ${global.decimalFormat(numero.toFixed(2))}`;
}

export function formatNumber(value, decimales = 1) {
    const numero = Number(value) || 0;
    return global.decimalFormat(numero.toFixed(decimales));
}

// Ejes de montos: con valores chicos (< 1000) dividir entre 1000 siempre
// da "0K"; en ese caso se muestra el numero tal cual.
export function formatEjeMonto(value) {
    const numero = Number(value) || 0;
    return Math.abs(numero) >= 1000 ? `${Math.round(numero / 1000)}K` : `${Math.round(numero)}`;
}

const MAX_CARACTERES_NOMBRE = 18;

// Recorta nombres largos para que la leyenda mantenga siempre el mismo
// ancho/tamano; sin esto, una categoria con nombre muy largo estira la
// leyenda y deforma el layout del grafico.
export function truncarNombre(nombre) {
    if (!nombre) return "";
    return nombre.length > MAX_CARACTERES_NOMBRE
        ? `${nombre.slice(0, MAX_CARACTERES_NOMBRE)}...`
        : nombre;
}

function direccionTendencia(actual, anterior) {
    if (actual > anterior) return "up";
    if (actual < anterior) return "down";
    return "flat";
}

// % de variacion actual vs anterior. Si el periodo anterior no tuvo
// movimiento, se evita la division entre 0: sin cambio real (0/0) queda
// plano, y una aparicion nueva (valor/0) se marca como +100%.
function calcularTendenciaPct(actual, anterior) {
    if (!anterior) return actual > 0 ? 100 : 0;
    return ((actual - anterior) / anterior) * 100;
}

function construirKpi(actual, anterior) {
    const valorActual = Number(actual) || 0;
    const valorAnterior = Number(anterior) || 0;
    return {
        valor: valorActual,
        tendenciaPct: calcularTendenciaPct(valorActual, valorAnterior),
        tendenciaDireccion: direccionTendencia(valorActual, valorAnterior),
    };
}

// "valores" trae el saldo valorizado del inventario: una fila resumen del
// periodo anterior (esDetalle = 0) y una fila por dia/mes del periodo
// filtrado (esDetalle = 1), con la etiqueta ya armada en "periodo".
// Como es un saldo (no un acumulado de movimientos), el valor del periodo
// es el del ultimo punto, y se compara contra el cierre del periodo anterior.
function construirValorInventario(valores) {
    const anterior = valores.find((fila) => Number(fila.esDetalle) !== 1) || {};
    const detalle = valores.filter((fila) => Number(fila.esDetalle) === 1);
    const ultimo = detalle[detalle.length - 1] || {};

    const construirMoneda = (campo) => ({
        ...construirKpi(ultimo[campo], anterior[campo]),
        serie: detalle.map((fila) => Number((Number(fila[campo]) || 0).toFixed(2))),
    });

    return {
        timeLine: detalle.map((fila) => fila.periodo),
        pen: construirMoneda("valorTotalSoles"),
        usd: construirMoneda("valorTotalDolares"),
    };
}

// "rotaciones" llega ordenado por mes; el ultimo es el mes actual y la
// tendencia es la diferencia (en veces) contra el mes previo.
function construirRotacion(rotaciones) {
    const valores = rotaciones.map((fila) => Number((Number(fila.rotacionVeces) || 0).toFixed(2)));
    const actual = valores[valores.length - 1] || 0;
    const anterior = valores[valores.length - 2] || 0;
    return {
        meses: rotaciones.map((fila) => fila.mes),
        valores,
        valor: actual,
        tendenciaValor: actual - anterior,
        tendenciaDireccion: direccionTendencia(actual, anterior),
    };
}

function construirCategorias(categorias) {
    const total = categorias.reduce((acumulado, fila) => acumulado + (Number(fila.cantidad) || 0), 0);
    const datos = categorias
        .map((fila) => {
            const cantidad = Number(fila.cantidad) || 0;
            return {
                nombre: fila.categoria,
                cantidad,
                porcentaje: total > 0 ? (cantidad / total) * 100 : 0,
            };
        })
        .sort((a, b) => b.cantidad - a.cantidad);
    return { total, datos };
}

export function buildDashboardData(response) {
    // El servicio (inventarioService.dashboardInventario) devuelve:
    // - valores: saldo valorizado por dia/mes + fila del periodo anterior.
    // - rotaciones: rotacion de inventario (veces) por mes.
    // - categorias: cantidad de productos por categoria.
    // - kpis: arreglo de un solo elemento con los conteos de productos del
    //   periodo actual (sufijo "_Actual") y del anterior ("_Anterior").
    const valores = (response && response.valores) || [];
    const rotaciones = (response && response.rotaciones) || [];
    const categorias = (response && response.categorias) || [];
    const kpisApi = (response && response.kpis && response.kpis[0]) || {};

    return {
        kpis: {
            productosTotales: construirKpi(kpisApi.totales_Actual, kpisApi.totales_Anterior),
            productosConStock: construirKpi(kpisApi.conStock_Actual, kpisApi.conStock_Anterior),
            productosStockBajo: construirKpi(kpisApi.stockBajo_Actual, kpisApi.stockBajo_Anterior),
            productosSinStock: construirKpi(kpisApi.sinStock_Actual, kpisApi.sinStock_Anterior),
        },
        valorInventario: construirValorInventario(valores),
        rotacion: construirRotacion(rotaciones),
        categorias: construirCategorias(categorias),
    };
}
